import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/authentication/auth.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { LocalStorageService } from '@core/services/local-storage.service';
import { UtilsService } from '@core/services/utils/utils.service';
import { Base64 } from 'js-base64';

interface EcommerceItem {
  item_name: string;
  item_id: string;
  item_brand: string;
  item_category: string;
  item_list_name?: string;
  item_list_id?: string;
  price?: number;
  totalPrice?: number;
  quantity: number;
  index: number;
  discount?: number;
  _discount?: number;
  /** Nueva presentación (búsqueda / descontinuados); escenario directo */
  new_presentation_origin_type?: string;
  /** Nueva presentación — escenario indirecto ("Otros productos…") */
  item_origin_type?: string;
  /** SKU descontinuado reemplazado por la nueva presentación */
  replaced_item_id?: string;
  // Campos personalizados opcionales
  item_custom_field1?: any;
  item_custom_field2?: any;
  item_custom_field3?: any;
}

interface EcommerceItemWithPrices {
  item_name: string;
  item_id: string;
  item_brand: string;
  item_category: string;
  item_list_name?: string;
  item_list_id?: string;
  price: number;
  totalPrice: number;
  quantity: number;
  index: number;
  discount: number;
  _discount: number;
  new_presentation_origin_type?: string;
  item_origin_type?: string;
  replaced_item_id?: string;
}

interface PriceFields {
  price: number;
  totalPrice: number;
  discount: number;
}

interface PurchaseRequestSentItem {
  item_name: string;
  item_id: string;
  item_brand: string;
  item_category: string;
  item_list_name: string;
  item_list_id: string;
  quantity: number;
  index: number;
  discount: number;
  _discount: number;
  price: number;
  totalPrice: number;
  new_presentation_origin_type?: string;
  item_origin_type?: string;
  replaced_item_id?: string;
}

interface PurchaseFailedItem {
  item_name: string;
  item_id: string;
  item_brand: string;
  item_category: string;
  quantity: number;
  price: number;
  totalPrice: number;
  new_presentation_origin_type?: string;
  item_origin_type?: string;
  replaced_item_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GtmEventService {
  public typeDevice: any;
  public user: any;
  public sector: any;
  private _totalPrice: any;
  private _products: any;
  private _events: any = {
    replacedProducts: [],
    addProducts: [],
    suggestedProducts: [],
    prevTotalPrice: '',
    deletedProducts: [],
    changeProducts: [],
  };
  private _purchaseCarPriceFields: PriceFields[] | undefined;
  private readonly _scrollThresholdsFired: { [key: string]: number[] } = {};

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly deviceService: DeviceDetectorService,
    private readonly _authService: AuthService,
    private readonly router: Router,
    private readonly gtmService: GoogleTagManagerService,
    private readonly _localStorageService: LocalStorageService
  ) {
    this.sector = this.setSector();
    this.detectDeviceType();
  }

  set totalPrice(value: any) {
    this._totalPrice = value;
  }

  get totalPrice(): any {
    return this._totalPrice;
  }

  set events(value: any) {
    if (!this._events) {
      this._events = {
        replacedProducts: [],
        addProducts: [],
        suggestedProducts: [],
        prevTotalPrice: '',
        deletedProducts: [],
        changeProducts: [],
      };
    }
    this._events.replacedProducts = [
      ...this._events.replacedProducts,
      ...(value.replacedProducts || []),
    ];
    this._events.addProducts = [
      ...this._events.addProducts,
      ...(value.addProducts || []),
    ];
    this._events.suggestedProducts = [
      ...this._events.suggestedProducts,
      ...(value.suggestedProducts || []),
    ];
    if (value.prevTotalPrice) {
      this._events.prevTotalPrice = value.prevTotalPrice;
    }
    this._events.deletedProducts = [
      ...this._events.deletedProducts,
      ...(value.deletedProducts || []),
    ];
    this._events.changeProducts = [
      ...this._events.changeProducts,
      ...(value.changeProducts || []),
    ];
  }

  get events(): any {
    return this._events;
  }

  resetEvents(donde): void {
    this._events = {
      replacedProducts: [],
      addProducts: [],
      suggestedProducts: [],
      prevTotalPrice: [],
      deletedProducts: [],
      changeProducts: [],
    };
    // console.log('Events have been reset:', this._events);
  }

  set products(value: any) {
    this._products = value;
  }

  get products(): any {
    return this._products;
  }

  // Método para guardar los datos del carrito antes de limpiarlo para el evento purchaseCarEvent
  setPurchaseCarData(products: any[], priceFields: PriceFields[]) {
    this._products = products;
    this._purchaseCarPriceFields = priceFields;
  }

  get purchaseCarPriceFields(): PriceFields[] {
    return this._purchaseCarPriceFields || [];
  }

  private detectDeviceType(): void {
    if (this.deviceService.isDesktop()) {
      this.typeDevice = 'Desktop';
    } else if (this.deviceService.isMobile()) {
      this.typeDevice = 'Mobile';
    } else if (this.deviceService.isTablet()) {
      this.typeDevice = 'Tablet';
    } else {
      this.typeDevice = 'Desconocido';
    }
  }

  setSector() {
    const userType = this.getUserType();
    
    if (userType === 'Vendedor') {
      // Para vendedores: el sector viene del cliente seleccionado como código
      const sectorCode = this._localStorageService.getItem('clientSelect')?.sector;
      return this.mapSectorCodeToName(sectorCode);
    } else {
      // Para clientes: el sector viene del sectorSelect
      return this._localStorageService.getItem('sectorSelect')?.name || undefined;
    }
  }

  mapSectorCodeToName(code: string): string | undefined {
    if (!code) return undefined;
    
    const sectorMap: { [key: string]: string } = {
      '0010': 'Core',
      '0007': 'Intradevco',
      '0012': 'Soluciones',
      '0070': 'Value',
      '0030': 'Nutricion'
    };
    
    return sectorMap[code] || undefined;
  }

  setUser() {
    return this._localStorageService.getItem('user')?.code || undefined;
  }

  setSeller() {
    return this.setUser();
  }

  setClient() {
    return this._localStorageService.getItem('clientSelect')?.code || undefined;
  }

  getUserType() {
    return this._localStorageService.getItem('user')?.type || undefined;
  }

  setCarsStorage() {
    const data: EcommerceItem[] = [];
    const cars = this._localStorageService.getItem('cars');
    const clientSelect = this._localStorageService.getItem('clientSelect');
    const list = this._localStorageService.getItem('listProductSearch') || [];
    let coreUnitBussiness
    if (clientSelect.name) {
      coreUnitBussiness = cars?.filter(
        (item) => item.unitBussines === clientSelect.name
      );
    } else {
      const sector = this.setSector();
      coreUnitBussiness = cars?.filter(
      (item) => item.unitBussines === sector
    );
    }
    
    coreUnitBussiness[0]?.products?.map((product: any, index: any) => {
      const enriched = this.enrichSingleProductNewPresentation(product, list);
      const payload: EcommerceItem = {
        item_name: enriched.description,
        item_id: String(this.currentToNumber(enriched?.sku)),
        item_brand: enriched.brandName || enriched.brand_name,
        item_category: enriched.categoryName || enriched.category_name,
        item_list_name: enriched.item_list_name || 'Generales',
        item_list_id: enriched.item_list_id || 'GENERAL',
        price: this.currentToNumber(enriched.discountedPrice) || this.currentToNumber(enriched.totalCashPrice),
        totalPrice: enriched.discountedPrice
          ? this.currentToNumber(enriched.totalPrice)
          : this.currentToNumber(enriched.totalPriceWithCash),
        quantity: typeof enriched.count === 'string'
          ? Number.parseInt(enriched.count.replace(',', ''), 10)
          : enriched.count,
        index: index || 1,
        discount: enriched.discount || (
          (this.currentToNumber(enriched.discountedPrice) || this.currentToNumber(enriched.totalCashPrice)) *
          (typeof enriched.count === 'string'
            ? Number.parseInt(enriched.count.replace(',', ''), 10)
            : enriched.count
          ) -
          (enriched.discountedPrice
            ? this.currentToNumber(enriched.totalPrice)
            : this.currentToNumber(enriched.totalPriceWithCash))
        ),
        _discount: enriched.discount || (
          (this.currentToNumber(enriched.discountedPrice) || this.currentToNumber(enriched.totalCashPrice)) *
          (typeof enriched.count === 'string'
            ? Number.parseInt(enriched.count.replace(',', ''), 10)
            : enriched.count
          ) -
          (enriched.discountedPrice
            ? this.currentToNumber(enriched.totalPrice)
            : this.currentToNumber(enriched.totalPriceWithCash))
        ),
        ...this.pickPresentationFieldsFromProduct(enriched),
      };      
      data.push(payload);
    });
    return data;
  }

  setCars(products) {
    const carState = this.setCarsStorage();
    const data: EcommerceItem[] = [];
    const list = this._localStorageService.getItem('listProductSearch') || [];
    if (products.length) {
      const patched = this.enrichProductsArrayForPresentation(products);
      patched.forEach((product, index) => {
        let updatedProduct = { ...product };
        if (updatedProduct.InvoicePrice) {
          updatedProduct.count = updatedProduct.InvoicePrice.quantity;
          updatedProduct.discountedPrice = updatedProduct.InvoicePrice.price;
          updatedProduct.totalPrice = updatedProduct.InvoicePrice.subTotal;
        }

        const payload = {
          item_name: product?.description,
          item_id: String(this.currentToNumber(product?.sku)),
          item_brand: product?.brandName || product?.brand_name,
          item_category: product?.categoryName || product?.category_name,
          item_list_name: product?.item_list_name || 'Generales',
          item_list_id: product?.item_list_id || 'GENERAL',
          quantity: typeof updatedProduct.count == 'string'
            ? Number.parseInt(updatedProduct.count.replace(',', ''), 10)
            : updatedProduct.count,
          index: index,
          discount: product?.discount || 0,
          _discount: product?.discount || 0,
          ...this.pickPresentationFieldsFromProduct(product),
        };
        data.push(payload);
      });
      return data;
    } else {
      let _i: any;
      carState.forEach((item) => {
        if (products.sku == item.item_id) {
          _i = item.index;
        }
      });

      const enriched = this.enrichSingleProductNewPresentation(products, list);
      const payload = {
        item_name: enriched.description,
        item_id: String(this.currentToNumber(enriched?.sku)),
        item_brand: enriched.brandName || enriched.brand_name,
        item_category: enriched.categoryName || enriched.category_name,
        item_list_name: enriched.listName || '',
        item_list_id: enriched.listId || '',
        quantity: typeof enriched.count === 'string'
          ? Number.parseInt(enriched.count.replace(',', ''), 10)
          : enriched.count,
        index: _i || 1,
        discount: enriched.discount || 0,
        _discount: enriched.discount || 0,
        ...this.pickPresentationFieldsFromProduct(enriched),
      };      
      data.push(payload);
      return data;
    }
  }

  currentToNumber(current: any): number {
    if (typeof current === 'string') {
      return Number(current.replace(/,/g, '').trim());
    }
  
    if (typeof current === 'number') {
      return current;
    }
    return 0;
  }

  /**
   * Nueva presentación desde flujo de búsqueda (descontinuados) — dataLayer por ítem.
   * @see doc "Medición de nuevas presentaciones desde el flujo de búsqueda"
   */
  private pickPresentationFieldsFromProduct(product: any): Partial<EcommerceItem> {
    if (!product) {
      return {};
    }
    const rid =
      product.replaced_item_id ?? product.replacedItemId ?? product.discontinuedSku;
    const out: Partial<EcommerceItem> = {};
    if (rid != null && rid !== '') {
      out.replaced_item_id = String(rid);
    }
    let npt = product.new_presentation_origin_type;
    let iot = product.item_origin_type;
    if (!npt && !iot && out.replaced_item_id) {
      if (product.gtmPresentationOrigin === 'others') {
        iot = 'Seleccion indirecta de nueva presentación';
      } else {
        npt = 'Seleccion directa de nueva presentación';
        iot = npt;
      }
    }
    if (npt) {
      out.new_presentation_origin_type = String(npt);
    }
    if (iot) {
      out.item_origin_type = String(iot);
    }
    return out;
  }

  /**
   * Replica metadatos de nueva presentación en productos del carrito para todos los eventos ecommerce.
   * Público para reutilizar en pantallas (ej. confirmación) antes de armar payloads.
   */
  enrichProductsForNewPresentationGtm(products: any[]): any[] {
    return this.enrichProductsArrayForPresentation(products);
  }

  private static readonly STORAGE_SKIP_VIEW_SEARCH_AFTER_AUTOCOMPLETE =
    'alicorp_gtm_skip_view_search_result';

  /**
   * Marca navegación post-clic desde autocompletado con Paso 3 (nueva presentación directa).
   * La pantalla de resultados debe omitir `viewSearchResultEvent` para no duplicar el virtualEvent.
   */
  scheduleSkipViewSearchResultEventAfterAutocomplete(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(
        GtmEventService.STORAGE_SKIP_VIEW_SEARCH_AFTER_AUTOCOMPLETE,
        '1'
      );
    }
  }

  /** Lee y borra la marca; true = no emitir `viewSearchResultEvent` en esta carga */
  consumeSkipViewSearchResultEventScheduled(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const key =
      GtmEventService.STORAGE_SKIP_VIEW_SEARCH_AFTER_AUTOCOMPLETE;
    if (sessionStorage.getItem(key) !== '1') {
      return false;
    }
    sessionStorage.removeItem(key);
    return true;
  }

  /**
   * SKU descontinuado reemplazado por nueva presentación (lista autocomplete / listProductSearch).
   */
  findReplacedItemIdForSearchAutocomplete(product: any, list: any[]): string | undefined {
    if (!product?.isRecommended || !list?.length) {
      return undefined;
    }
    const disc = list.find(
      (p: any) =>
        p?.discontinued && UtilsService.skuValuesEqual(p.newSku, product.sku)
    );
    return disc?.sku != null ? String(disc.sku) : undefined;
  }

  /**
   * Metadatos GTM (nueva presentación directa desde barra de búsqueda) para reutilizar en header y resultado.
   */
  buildPresentationMetaFromSearchAutocomplete(
    product: any,
    list: any[] | null | undefined
  ):
    | {
        item_origin_type: string;
        new_presentation_origin_type: string;
        replaced_item_id: string;
      }
    | undefined {
    const npLabel = 'Seleccion directa de nueva presentación';
    const replaced = this.findReplacedItemIdForSearchAutocomplete(
      product,
      list || []
    );
    if (!product?.isRecommended || !replaced) {
      return undefined;
    }
    return {
      item_origin_type: npLabel,
      new_presentation_origin_type: npLabel,
      replaced_item_id: replaced,
    };
  }

  private enrichProductsArrayForPresentation(products: any): any[] {
    if (!Array.isArray(products)) {
      return [];
    }
    if (!products.length) {
      return products;
    }
    const list = this._localStorageService.getItem('listProductSearch') || [];
    return products.map((p) => this.enrichSingleProductNewPresentation(p, list));
  }

  private enrichSingleProductNewPresentation(product: any, list: any[]): any {
    if (!product?.isRecommended) {
      return product;
    }
    const out = { ...product };
    const hasRid =
      (out.replaced_item_id != null && String(out.replaced_item_id).trim() !== '') ||
      (out.replacedItemId != null && String(out.replacedItemId).trim() !== '');
    if (!hasRid && list?.length) {
      const disc = list.find(
        (p: any) =>
          p?.discontinued && UtilsService.skuValuesEqual(p.newSku, out.sku)
      );
      if (disc?.sku != null && disc.sku !== '') {
        out.replaced_item_id = String(disc.sku);
      }
    }
    const rid = out.replaced_item_id ?? out.replacedItemId;
    if (rid == null || String(rid).trim() === '') {
      return out;
    }
    const labelDirect = 'Seleccion directa de nueva presentación';
    const labelIndirect = 'Seleccion indirecta de nueva presentación';
    if (!out.new_presentation_origin_type && !out.item_origin_type) {
      if (out.gtmPresentationOrigin === 'others') {
        out.item_origin_type = labelIndirect;
      } else {
        // 'selected', flag perdido tras orderPrice/store, o flujo directo típico (doc escenario 1)
        out.new_presentation_origin_type = labelDirect;
        out.item_origin_type = labelDirect;
      }
    }
    return out;
  }

  calculateDiscount(product: any): number {
    // Si existe InvoicePrice, usar esos datos actualizados
    if (product.InvoicePrice) {
      const finalPrice = this.currentToNumber(product.InvoicePrice.price);
      const originalPrice = this.currentToNumber(product.salePrice) || this.currentToNumber(product.cashPrice) || this.currentToNumber(product.priceAnticipated);
      return Number.parseFloat((originalPrice - finalPrice).toFixed(2));
    }
    
    // Si hay discountedPrice, calcular el descuento
    if (product.discountedPrice) {
      const finalPrice = this.currentToNumber(product.discountedPrice);
      const originalPrice = this.currentToNumber(product.salePrice) || this.currentToNumber(product.cashPrice) || this.currentToNumber(product.priceAnticipated);
      return Number.parseFloat((originalPrice - finalPrice).toFixed(2));
    }
    
    // Si no hay descuento específico, retornar 0
    return 0;
  }

  addPriceFieldsToItems(items: EcommerceItem[], products: any[], customItemFields?: any): EcommerceItem[] {
    const patchedProducts = Array.isArray(products)
      ? products.length
        ? this.enrichProductsArrayForPresentation(products)
        : products
      : products;
    return items.map((item, index) => {
      const product = patchedProducts[index] || patchedProducts;
      
      // Usar precios con descuento si están disponibles
      let finalPrice = this.currentToNumber(product.discountedPrice) || this.currentToNumber(product.salePrice) || this.currentToNumber(product.cashPrice);
      let finalTotalPrice = this.currentToNumber(product.totalPrice);
      let finalDiscount = this.calculateDiscount(product);

      // Si existe InvoicePrice, usar esos datos actualizados
      if (product.InvoicePrice) {
        finalPrice = this.currentToNumber(product.InvoicePrice.price);
        finalTotalPrice = this.currentToNumber(product.InvoicePrice.subTotal);
        
        // Calcular el descuento: precio original - precio con descuento
        const originalPrice = this.currentToNumber(product.salePrice) || this.currentToNumber(product.cashPrice) || this.currentToNumber(product.priceAnticipated);
        finalDiscount = Number.parseFloat((originalPrice - finalPrice).toFixed(2));
      }

      return {
        ...item,
        price: finalPrice,
        totalPrice: finalTotalPrice,
        discount: finalDiscount,
        _discount: finalDiscount,
        ...this.pickPresentationFieldsFromProduct(product),
        // Campos personalizados para cada item
        item_custom_field1: customItemFields?.field1 || product.customField1 || 'default',
        item_custom_field2: customItemFields?.field2 || product.customField2 || 0,
        item_custom_field3: customItemFields?.field3 || product.customField3 || false,
      };
    });
  }

  async pushEvent(eventData: any, event?: string) {
    this.user = this._authService.getUser();
    if (this.user) {
      // Campos que pueden ser undefined si no vienen en eventData
      const defaultFields = [
        'ProcesoGeneral',
        'ProcesoGeneralBusqueda',
        'ProcesoEspecificoBusqueda',
        'ProcesoGeneralCompra',
        'ProcesoEspecificoCompra',
        'CategoriaProducto',
        'MarcaProducto',
        'BusquedaSeleccionada',
        'CTA',
        'Seccion',
        'SubSeccion',
        'Paso',
        'TextoExito',
        'TextoError',
        'TextoAdvertencia',
        'CantidadProductosEnCarrito',
        'CantidadNuevaProductosEnCarrito',
        'NombreArchivo',
        'Intencion',
        'CantidadProductoSugerido',
        'PrecioTotalProductoSugerido',
        'EstadoDeSugerencia',
        'ProductoSugerido',
        'PrecioTotalProductoSolicitado',
        'CantidadProductoSolicitado',
        'ProductoNoDisponible',
        'ProductoSugeridoAceptado',
        'CantidadProductoSugeridoAceptado',
        'PrecioProductoSugeridoAceptado',
        'PrecioProductoEliminado',
        'MontoTotalAnterior',
        'MontoTotalNuevo',
        'Mensaje',
        'TextoProducto',
        'Pedido',
        'Estado',
        'SKUSolicitado',
        'DescSKU',
        'Cantidad',
        'StockMax',
        'MontoPrecio',
        'Accion',
        'SKUReemplazante',
        'DescSKUReemplazado',
        'CantidadReemplazado',
        'MontoPrecioSugerido',
        'items',
        'transaction_id',
        'value',
        'currency',
        'item_origin_type',
        'new_presentation_origin_type',
        'replaced_item_id',
        'filter_name',
        'filter_type',
        'filter_label',
        'control_type',
        'file_extension',
        'file_name',
        'link_text',
        'percent_scrolled',
        'search_term',
        'UnidadMedida',
      ];

      const defaultValues = defaultFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: undefined,
        }),
        {}
      );

      // Obtener el tipo de usuario actualizado directamente del localStorage
      const userType = this.getUserType();

      // Determinar UserId y SellerId según el tipo de usuario
      let userId: string | undefined;
      let sellerId: string | undefined;

      if (userType === 'Vendedor') {
        // Para vendedores: SellerId es el código del vendedor, UserId es el código del cliente seleccionado
        const sellerCode = this.setSeller();
        sellerId = sellerCode ? Base64.encode(sellerCode) : undefined;
        
        const clientCode = this.setClient();
        userId = clientCode ? Base64.encode(clientCode) : undefined;
      } else {
        // Para clientes: SellerId es undefined, UserId es el código del usuario
        sellerId = undefined;
        const userCode = this.setUser();
        userId = userCode ? Base64.encode(userCode) : undefined;
      }

      // Primero establecemos los valores base que siempre deben existir
      const modifiedEventData = {
        event: event ? event : 'virtualEvent',
        TimeStamp: new Date().getTime(),
        Url: this.document.location.href,
        TipoDispositivo: this.typeDevice,
        PagePath: this.router.url,
        TipoUsuario: userType,
        UserId: userId,
        SellerId: sellerId,
        UnidadDeNegocio: this.setSector(),
        ...defaultValues, // Agregamos los campos opcionales con undefined
        ...eventData, // Sobrescribimos con los valores que vengan en eventData
      };
      // console.table(eventData)
      this.gtmService.pushTag(modifiedEventData);
    }
  }

  /**
   * Evento de cambio de filtro: dropdowns, radios o cambios de vista.
   * Solo se envían los campos provistos.
   */
  filterChangeEvent(params: {
    filterType: string;
    filterLabel?: string | null;
    controlType: string;
    filterName?: string;
    seccion?: string;
    subSeccion?: string;
  }): void {
    const eventData: any = {
      filter_type: params.filterType,
      filter_label: params.filterLabel,
      control_type: params.controlType,
    };
    if (params.filterName) {
      eventData.filter_name = params.filterName;
    }
    if (params.seccion) {
      eventData.Seccion = params.seccion;
    }
    if (params.subSeccion) {
      eventData.SubSeccion = params.subSeccion;
    }
    this.pushEvent(eventData, 'filter_change');
  }

  /**
   * Evento de descarga de archivo. maskTokens contiene los valores sensibles
   * (código de cliente / RUC) que deben anonimizarse en el file_name enviado
   * a analítica; el archivo real que descarga el usuario no se altera.
   */
  fileDownloadEvent(params: {
    fileExtension: string;
    fileName: string;
    linkText: string;
    seccion?: string;
    subSeccion?: string;
    maskTokens?: (string | number | undefined | null)[];
  }): void {
    const fileName =
      params.maskTokens && params.maskTokens.length
        ? this.anonymizeFileName(params.fileName, params.maskTokens)
        : params.fileName;
    const eventData: any = {
      file_extension: params.fileExtension,
      file_name: fileName,
      link_text: params.linkText,
    };
    if (params.seccion) {
      eventData.Seccion = params.seccion;
    }
    if (params.subSeccion) {
      eventData.SubSeccion = params.subSeccion;
    }
    this.pushEvent(eventData, 'file_download');
  }

  /**
   * Evento genérico de clic (virtualEvent). Solo se envían los campos provistos.
   */
  clickElementEvent(params: {
    cta?: string;
    seccion?: string;
    subSeccion?: string;
    controlType?: string;
    accion?: string;
    unidadMedida?: string;
    categoriaProducto?: string;
    textoProducto?: string;
    procesoGeneralCompra?: string;
    procesoEspecificoCompra?: string;
    paso?: string;
  }): void {
    const eventData: any = {};
    if (params.cta) {
      eventData.CTA = params.cta;
    }
    if (params.seccion) {
      eventData.Seccion = params.seccion;
    }
    if (params.subSeccion) {
      eventData.SubSeccion = params.subSeccion;
    }
    if (params.controlType) {
      eventData.control_type = params.controlType;
    }
    if (params.accion) {
      eventData.Accion = params.accion;
    }
    if (params.unidadMedida) {
      eventData.UnidadMedida = params.unidadMedida;
    }
    if (params.categoriaProducto) {
      eventData.CategoriaProducto = params.categoriaProducto;
    }
    if (params.textoProducto) {
      eventData.TextoProducto = params.textoProducto;
    }
    if (params.procesoGeneralCompra) {
      eventData.ProcesoGeneralCompra = params.procesoGeneralCompra;
    }
    if (params.procesoEspecificoCompra) {
      eventData.ProcesoEspecificoCompra = params.procesoEspecificoCompra;
    }
    if (params.paso) {
      eventData.Paso = params.paso;
    }
    this.pushEvent(eventData);
  }

  /**
   * Evento de inicio de formulario: primera interacción del usuario con un campo.
   */
  formStartEvent(params: {
    seccion?: string;
    subSeccion?: string;
    categoriaProducto?: string;
    textoProducto?: string;
  }): void {
    const eventData: any = {};
    if (params.seccion) {
      eventData.Seccion = params.seccion;
    }
    if (params.subSeccion) {
      eventData.SubSeccion = params.subSeccion;
    }
    if (params.categoriaProducto) {
      eventData.CategoriaProducto = params.categoriaProducto;
    }
    if (params.textoProducto) {
      eventData.TextoProducto = params.textoProducto;
    }
    this.pushEvent(eventData, 'form_start');
  }

  /**
   * Evento de envío exitoso de formulario.
   */
  formSubmitEvent(params: {
    cta?: string;
    seccion?: string;
    subSeccion?: string;
    categoriaProducto?: string;
    textoProducto?: string;
  }): void {
    const eventData: any = {};
    if (params.cta) {
      eventData.CTA = params.cta;
    }
    if (params.seccion) {
      eventData.Seccion = params.seccion;
    }
    if (params.subSeccion) {
      eventData.SubSeccion = params.subSeccion;
    }
    if (params.categoriaProducto) {
      eventData.CategoriaProducto = params.categoriaProducto;
    }
    if (params.textoProducto) {
      eventData.TextoProducto = params.textoProducto;
    }
    this.pushEvent(eventData, 'form_submit');
  }

  /**
   * Evento de búsqueda. Debe dispararse con debounce (2s) y mínimo 2 caracteres
   * desde el componente que contiene el buscador.
   */
  searchEvent(term: string, seccion?: string): void {
    const eventData: any = { search_term: term };
    if (seccion) {
      eventData.Seccion = seccion;
    }
    this.pushEvent(eventData, 'search');
  }

  /**
   * Registra el progreso de scroll y dispara el evento 'scroll' UNA sola vez
   * por cada umbral alcanzado (50, 75, 90). `key` aísla el estado por vista;
   * llamar resetScrollProgress(key) al inicializar cada vista.
   */
  trackScrollProgress(
    key: string,
    metrics: { scrollTop: number; scrollHeight: number; clientHeight: number },
    seccion: string,
    subSeccion?: string
  ): void {
    const scrollable = metrics.scrollHeight - metrics.clientHeight;
    // Solo medir scroll si el contenido supera el alto visible por un margen real
    // (evita que en páginas apenas más altas que la pantalla salten los 3 umbrales a la vez).
    if (scrollable < metrics.clientHeight * 0.5) {
      return;
    }
    const percent = (metrics.scrollTop / scrollable) * 100;
    if (!this._scrollThresholdsFired[key]) {
      this._scrollThresholdsFired[key] = [];
    }
    const fired = this._scrollThresholdsFired[key];
    [50, 75, 90].forEach((threshold) => {
      if (percent >= threshold && fired.indexOf(threshold) === -1) {
        fired.push(threshold);
        const data: any = { percent_scrolled: threshold, Seccion: seccion };
        if (subSeccion) {
          data.SubSeccion = subSeccion;
        }
        this.pushEvent(data, 'scroll');
      }
    });
  }

  /** Reinicia el estado de umbrales de scroll de una vista. */
  resetScrollProgress(key: string): void {
    this._scrollThresholdsFired[key] = [];
  }

  /**
   * Enmascara datos personales (código de cliente / RUC) dentro del nombre de
   * archivo que se envía a analítica, reemplazándolos por ceros de igual largo.
   */
  private anonymizeFileName(
    fileName: string,
    tokens: (string | number | undefined | null)[]
  ): string {
    let result = fileName || '';
    (tokens || []).forEach((token) => {
      if (token === undefined || token === null) {
        return;
      }
      const value = String(token).trim();
      if (value.length < 4) {
        return;
      }
      const mask = '0'.repeat(value.length);
      result = result.split(value).join(mask);
    });
    return result;
  }

  loginEvent(): void {
    const eventData = {
      ProcesoGeneral: 'Login',
      CTA: 'Ingresar',
      Seccion: 'Login',
      SubSeccion: 'Boton login',
    };
    this.pushEvent(eventData);
  }

  clickCategoriesEvent() {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por despliegue de categorías',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por despliegue de categorías',
      CTA: 'Categorias',
      Seccion: 'Header',
      SubSeccion: 'Menú categorias',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  clickCategoryEvent(
    category: string,
    meta?: {
      item_origin_type?: string;
      new_presentation_origin_type?: string;
      replaced_item_id?: string;
    }
  ) {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por despliegue de categorias',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por despliegue de categorias',
      CategoriaProducto: category,
      Seccion: 'Header',
      SubSeccion: 'Menu categorias',
      Paso: 'Paso 2',
      ...meta,
    };
    this.pushEvent(eventData);
  }

  clickBrandEvent(
    brand: string,
    meta?: {
      item_origin_type?: string;
      new_presentation_origin_type?: string;
      replaced_item_id?: string;
    }
  ) {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por despliegue de categorias',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por despliegue de categorias',
      MarcaProducto: brand,
      Seccion: 'Header',
      SubSeccion: 'Menu categorias',
      Paso: 'Paso 3',
      ...meta,
    };
    this.pushEvent(eventData);
  }

  viewProductsEvent(category: string, brand: string) {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por despliegue de categorias',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por despliegue de categorias',
      Seccion: category,
      SubSeccion: brand,
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }

  clickSearchBarEvent() {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por barra de busqueda',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por barra de busqueda',
      Seccion: 'Header',
      SubSeccion: 'Barra de busqueda',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  /**
   * Clic en resultado del autocompletado (Paso 2 salvo nueva presentación directa → Paso 3).
   */
  clickSearchResultEvent(
    optionSelected: string,
    meta?: {
      item_origin_type?: string;
      new_presentation_origin_type?: string;
      replaced_item_id?: string;
    }
  ) {
    const isNuevaPresentacionDirecta = !!(
      meta?.replaced_item_id &&
      (meta?.item_origin_type || meta?.new_presentation_origin_type)
    );
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por barra de busqueda',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por barra de busqueda',
      BusquedaSeleccionada: optionSelected,
      Seccion: 'Header',
      SubSeccion: 'Barra de busqueda',
      Paso: isNuevaPresentacionDirecta ? 'Paso 3' : 'Paso 2',
      ...meta,
    };
    this.pushEvent(eventData);
  }

  viewSearchResultEvent(
    brand: string,
    category: string,
    result: string,
    meta?: {
      item_origin_type?: string;
      new_presentation_origin_type?: string;
      replaced_item_id?: string;
    }
  ) {
    const eventData = {
      ProcesoGeneralBusqueda: 'Búsqueda de productos',
      ProcesoEspecificoBusqueda: 'Por barra de busqueda',
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Por barra de busqueda',
      BusquedaSeleccionada: result,
      Seccion: category,
      SubSeccion: brand,
      Paso: 'Paso 3',
      ...meta,
    };
    this.pushEvent(eventData);
  }

  clickHomeEvent() {
    // Obtener el usuario directamente del AuthService
    const currentUser = this._authService.getUser();
    
    // Solo ejecutar si TipoUsuario es diferente a "Vendedor"
    if (currentUser?.type !== 'Vendedor') {
      const eventData = {
        CTA: 'Home',
        Seccion: 'Header',
        SubSeccion: 'Home',
        Paso: 'Paso 1',
      };
      this.pushEvent(eventData);
    }
  }

  clickHomeEventSeller(cta: string) {
    // Obtener el usuario directamente del AuthService
    const currentUser = this._authService.getUser();
    
    // Solo ejecutar si TipoUsuario es "Vendedor"
    if (currentUser?.type === 'Vendedor') {
      const eventData = {
        CTA: cta,
        Seccion: 'Header',
        SubSeccion: 'Home',
        Paso: 'Paso 1',
      };
      this.pushEvent(eventData);
    }
  }

  clickUploadOrderEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      CTA: 'Subir pedido',
      Seccion: 'Header',
      SubSeccion: 'Subir pedido',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  clickAddButtonEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      CTA: 'Agregar',
      Seccion: 'Subir pedido',
      SubSeccion: 'Agregar SKU',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  clickContinueButtonEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      CTA: 'Continuar',
      Seccion: 'Subir pedido',
      SubSeccion: 'PopUpSKU',
      Paso: 'Paso 3',
    };
    this.pushEvent(eventData);
  }

  viewVerificationOkEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      TextoExito: 'Verificación exitosa',
      Seccion: 'Subir pedido SKU',
      SubSeccion: 'PopUpExitoso',
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }

  viewWarningDisplaySkuEvent(warnings: string[]) {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      TextoAdvertencia: warnings,
      Seccion: 'Subir pedido',
      SubSeccion: 'PopUpAdvertencia',
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }

  viewErrorDisplaySkuEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Copiado y pegado de SKUs',
      TextoError: 'Algo salió mal!',
      Seccion: 'Subir pedido',
      SubSeccion: 'PopUpError',
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }

  uploadExcelFileEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga de excel',
      CTA: 'Subir archivo',
      Seccion: 'Subir pedido',
      SubSeccion: 'Subir archivo de excel',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  viewSuccessfulVerificationEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga de excel',
      TextoExito: 'Archivo subido con éxito',
      Seccion: 'Subir pedido Excel',
      SubSeccion: 'PopUpExitoso',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  viewWarmomgDisplayEvent(warnings: string[]) {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga de excel',
      TextoAdvertencia: warnings,
      Seccion: 'Subir pedido',
      SubSeccion: 'PopUpAdvertencia',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  viewWarmomgDisplay2Event() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga de excel',
      TextoError: 'La columna CANTIDAD está vacía.',
      Seccion: 'Subir pedido',
      SubSeccion: 'PopUpError',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  clickPurchaseHistoryEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      CTA: 'Historial de compras',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  viewPurchaseHistoryEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      Seccion: 'Historial de compras pasadas',
      SubSeccion: '',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  clickViewOrderButtonEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      CTA: 'Ver pedido',
      Seccion: 'Historial de compras pasadas',
      SubSeccion: 'Tabla de historial',
      Paso: 'Paso 3',
    };
    this.pushEvent(eventData);
  }
  viewOrderDetailEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      Seccion: 'Detalle de pedido',
      SubSeccion: '',
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }
  clickAddCartButtonEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      CTA: 'Añadir todo al carrito',
      Seccion: 'Detalle del pedido',
      SubSeccion: 'Número del pedido',
      Paso: 'Paso 5',
    };
    this.pushEvent(eventData);
  }

  viewPriceUpdateDisplayEvent() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      Seccion: 'Actualización de precios',
      SubSeccion: 'PopUpExitoso',
      Paso: 'Paso 6',
    };
    this.pushEvent(eventData);
  }

  viewPriceUpdateDisplay2Event() {
    const eventData = {
      ProcesoGeneralCompra: 'Compra de productos',
      ProcesoEspecificoCompra: 'Carga por compras pasadas',
      Seccion: 'Productos no disponibles',
      SubSeccion: 'PopUpAdvertencia',
      Paso: 'Paso 6',
    };
    this.pushEvent(eventData);
  }

  clickMyAccountEvent() {
    const eventData = {
      ProcesoGeneral: 'Interacción con el header',
      CTA: 'Mi cuenta',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  clickMyCarEvent() {
    const eventData = {
      ProcesoGeneral: 'Gestion del carrito',
      CTA: 'Mi carrito',
      Seccion: 'Header',
      SubSeccion: 'Mi carrito',
    };
    this.pushEvent(eventData);
  }

  clickDownloadCatalogueEvent() {
    const eventData = {
      ProcesoGeneral: 'Gestión del carrito',
      CTA: 'Descargar catálogo',
      Seccion: 'Subir pedido',
      SubSeccion: 'Catálogo de productos con precio',
    };
    this.pushEvent(eventData);
  }

  clickCheckAvailabilityEvent(message: string) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      Mensaje: message,
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'Comprueba el stock',
      Paso: 'Paso 1',
    };
    this.pushEvent(eventData);
  }

  errorAvailabilityEvent() {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoError:
        'No pudimos comprobar el stock de tus productos, vuelve a intentarlo refrescando tu navegador en unos minutos.',
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpError',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  clickEditCarEvent() {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      CTA: 'Editar carrito',
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'Editar carrito',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  suggestedViewEvent(product, count, price) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      CTA: 'Ver sugerencia',
      ProductoNoDisponible: product,
      CantidadProductoSolicitado: count,
      PrecioTotalProductoSolicitado: this.currentToNumber(price),
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'Ver sugerencia',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  replaceOrAddSuggestedEvent(
    type,
    texto,
    product,
    count,
    suggested,
    suggestedCount
  ) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      CTA: type,
      TextoProducto: texto,
      ProductoNoDisponible: product,
      CantidadProductoSolicitado: count,
      ProductoSugerido: suggested,
      CantidadProductoSugerido: this.currentToNumber(suggestedCount),
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'Elegir Sugerencia',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  withSuggestedAvailabilityEvent(
    texto,
    nombre,
    cantidad,
    precio,
    nombreSugerido,
    cantidadSugerida,
    precioSugerido,
    estado,
    montoanterior,
    montonuevo
  ) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoProducto: texto,
      ProductoNoDisponible: nombre,
      CantidadProductoSolicitado: cantidad,
      PrecioTotalProductoSolicitado: precio,
      ProductoSugerido: nombreSugerido,
      CantidadProductoSugerido: cantidadSugerida,
      PrecioTotalProductoSugerido: precioSugerido,
      EstadoDeSugerencia: estado,
      MontoTotalAnterior: montoanterior,
      MontoTotalNuevo: montonuevo,
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpAdvertenciaSugerido',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  //TODO: cuando no hay stock suficiente y se cambia la cantidad el evento no se debe mandar
  outOfStockOutOfReplaceEvent(texto, nombre, cantidad, precio) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoProducto: texto,
      ProductoNoDisponible: nombre,
      CantidadProductoSolicitado: cantidad,
      PrecioTotalProductoSolicitado: this.currentToNumber(precio),
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpAdvertencia',
      Paso: 'Paso 2',
    };
    this.pushEvent(eventData);
  }

  acceptedSuggestedProductEvent(nombre, cantidad, precio) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoExito: '¡Listo! Se aplicarán los siguientes cambios',
      ProductoSugeridoAceptado: nombre,
      CantidadProductoSugeridoAceptado: cantidad,
      PrecioProductoSugeridoAceptado: precio,
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpConfirmacionAceptado',
      Paso: 'Paso 3',
    };
    this.pushEvent(eventData);
  }

  deletedProductoWithoutStockEvent(nombre, cantidad, precio) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoExito: '¡Listo! Se aplicarán los siguientes cambios',
      ProductoEliminado: nombre,
      CantidadProductoEliminado: cantidad,
      PrecioProductoEliminado: precio,
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpConfirmacionEliminado',
      Paso: 'Paso 3',
    };
    this.pushEvent(eventData);
  }

  checkAvailabilityEvent(before, after) {
    const eventData = {
      ProcesoGeneral: 'Comprobar disponibilidad',
      TextoExito: 'Entendido',
      MontoTotalAnterior: before,
      MontoTotalNuevo: after,
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'PopUpConfirmacion',
      Paso: 'Paso 4',
    };
    this.pushEvent(eventData);
  }

  finishOrder() {
    const eventData = {
      ProcesoGeneral: 'Intención de Compra',
      CTA: 'Finalizar compra',
      Seccion: 'Confirmación de pedido',
      SubSeccion: 'Finalizar Compra',
    };
    this.pushEvent(eventData);
  }

  //event, TimeStamp, Url, TipoDispositivo, PagePath, TipoUsuario

  clickAdvanceSalesEvent() {
    const eventData = {
      ProcesoGeneral: 'Avance de ventas',
      CTA: 'Avance de ventas',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  viewAdvanceSalesEvent() {
    const eventData = {
      ProcesoGeneral: 'Avance de ventas',
      Seccion: 'Avance de ventas',
      SubSeccion: 'Descargas',
    };
    this.pushEvent(eventData);
  }

  downloadAdvanceSalesEvent(fileName: string) {
    const eventData = {
      ProcesoGeneral: 'Avance de ventas',
      NombreArchivo: fileName,
      Seccion: 'Avance de ventas',
      SubSeccion: 'Descargas',
    };
    this.pushEvent(eventData);
  }

  clickAwardsEvent() {
    const eventData = {
      ProcesoGeneral: 'Reconocimientos',
      CTA: 'Reconocimientos',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  viewAwardsEvent() {
    const eventData = {
      ProcesoGeneral: 'Reconocimientos',
      Seccion: 'Reconocimientos',
      SubSeccion: 'Descargas',
    };
    this.pushEvent(eventData);
  }

  downloadAwardsEvent(fileName: string) {
    const eventData = {
      ProcesoGeneral: 'Reconocimientos',
      NombreArchivo: fileName,
      Seccion: 'Reconocimientos',
      SubSeccion: 'Descargas',
    };
    this.pushEvent(eventData);
  }

  clickTrackEvent() {
    const eventData = {
      ProcesoGeneral: 'Seguimientos de pedido',
      CTA: 'Seguimiento',
      Seccion: 'Detalle de pedido',
      SubSeccion: '',
    };
    this.pushEvent(eventData);
  }

  viewOrderTrackingEvent() {
    const eventData = {
      ProcesoGeneral: 'Seguimientos de pedido',
      Seccion: 'Seguimiento de pedido',
      SubSeccion: '',
    };
    this.pushEvent(eventData);
  }

  clickCheckingAccountEvent() {
    const eventData = {
      ProcesoGeneral: 'Cuenta corriente',
      CTA: 'Cuenta corriente',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  viewCheckingAccountEvent() {
    const eventData = {
      ProcesoGeneral: 'Cuenta corriente',
      Seccion: 'Cuenta corriente',
      SubSeccion: '',
    };
    this.pushEvent(eventData);
  }

  clickElectronicReceiptEvent() {
    const eventData = {
      ProcesoGeneral: 'Cuenta corriente',
      CTA: 'Comprobante electrónico',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  clickAlliesEvent() {
    const eventData = {
      ProcesoGeneral: 'Aliados',
      CTA: 'Aliados',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  viewAlliesEvent() {
    const eventData = {
      ProcesoGeneral: 'Aliados',
      Seccion: 'Aliados',
      SubSeccion: '',
    };
    this.pushEvent(eventData);
  }

  viewClosingPageEvent() {
    const eventData = {
      ProcesoGeneral: 'Aliados',
      Seccion: 'Cierre',
      SubSeccion: '',
    };
    this.pushEvent(eventData);
  }

  clickPasswordChangeEvent() {
    const eventData = {
      ProcesoGeneral: 'Cambiar contraseña',
      CTA: 'Cambiar Contraseña',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta',
    };
    this.pushEvent(eventData);
  }

  // !Ecommerce Section

  addProductCartEvent(name: string, products?: any[], priceFields?: PriceFields[]) {
    const eventData = {
      Intencion: 'Añadir al carrito',
      CTA: name,
      ecommerce: {
        items: products && priceFields 
          ? this.combineItemsWithPriceFields(products, priceFields)
          : this.setCarsStorage(),
      },
    };
    this.pushEvent(eventData, 'add_to_cart');
  }

  deleteProductCarEvent(name: string, products?: any[], priceFields?: PriceFields[]) {
    const eventData = {
      Intencion: 'Remover del carrito',
      CTA: name,
      ecommerce: {
        items: products && priceFields 
          ? this.combineItemsWithPriceFields(products, priceFields)
          : this.setCarsStorage(),
      },
    };
    this.pushEvent(eventData, 'remove_from_cart');
  }

  checkoutCarEvent(products?: any[], priceFields?: PriceFields[]) {
    const eventData = {
      Intencion: 'Iniciar checkout',
      ecommerce: {
        items: products && priceFields 
          ? this.combineItemsWithPriceFields(products, priceFields)
          : this.setCarsWithInvoicePrice(),
      },
    };
    this.pushEvent(eventData, 'begin_checkout');
  }

  setCarsWithInvoicePrice() {
    const data: EcommerceItem[] = [];
    const cars = this._localStorageService.getItem('cars');
    const clientSelect = this._localStorageService.getItem('clientSelect');
    let coreUnitBussiness;
    if (clientSelect.name) {
      coreUnitBussiness = cars?.filter(
        (item) => item.unitBussines === clientSelect.name
      );
    } else {
      const sector = this.setSector();
      coreUnitBussiness = cars?.filter(
        (item) => item.unitBussines === sector
      );
    }
    
    const list = this._localStorageService.getItem('listProductSearch') || [];
    coreUnitBussiness[0]?.products?.map((product: any, index: any) => {
      const enriched = this.enrichSingleProductNewPresentation(product, list);
      // Usar InvoicePrice si está disponible, sino usar los datos originales
      let finalPrice = this.currentToNumber(enriched.discountedPrice) || this.currentToNumber(enriched.totalCashPrice);
      let finalTotalPrice = enriched.discountedPrice
        ? this.currentToNumber(enriched.totalPrice)
        : this.currentToNumber(enriched.totalPriceWithCash);
      let finalDiscount = enriched.discount || 0;

      // Si existe InvoicePrice, usar esos datos actualizados
      if (enriched.InvoicePrice) {
        finalPrice = this.currentToNumber(enriched.InvoicePrice.price);
        finalTotalPrice = this.currentToNumber(enriched.InvoicePrice.subTotal);
        
        // Calcular el descuento: precio original - precio con descuento
        const originalPrice = this.currentToNumber(enriched.salePrice) || this.currentToNumber(enriched.cashPrice) || this.currentToNumber(enriched.priceAnticipated);
        finalDiscount = Number.parseFloat((originalPrice - finalPrice).toFixed(2));
      }

      const payload: EcommerceItem = {
        item_name: enriched.description,
        item_id: String(this.currentToNumber(enriched?.sku)),
        item_brand: enriched.brandName || enriched.brand_name,
        item_category: enriched.categoryName || enriched.category_name,
        item_list_name: enriched.item_list_name || 'Generales',
        item_list_id: enriched.item_list_id || 'GENERAL',
        price: finalPrice,
        totalPrice: finalTotalPrice,
        quantity: typeof enriched.count === 'string'
          ? Number.parseInt(enriched.count.replace(',', ''), 10)
          : enriched.count,
        index: index || 1,
        discount: finalDiscount,
        _discount: finalDiscount,
        ...this.pickPresentationFieldsFromProduct(enriched),
      };      
      data.push(payload);
    });
    return data;
  }

  //event, TimeStamp, Url, TipoDispositivo, PagePath, TipoUsuario
  purchaseCarEvent(transaction: any, products?: any[], priceFields?: PriceFields[]) {
    // Usar los datos guardados del carrito si no se proporcionan productos específicos
    const rawFinal = products ?? this._products;
    const arr = Array.isArray(rawFinal)
      ? rawFinal
      : rawFinal != null
        ? [rawFinal]
        : [];
    const finalProducts = this.enrichProductsArrayForPresentation(arr);
    const finalPriceFields = priceFields || this._purchaseCarPriceFields || [];
    
    // Generar los items
    const items: any[] = finalProducts.length > 0 && finalPriceFields.length > 0
      ? this.combineItemsWithPriceFields(finalProducts, finalPriceFields)
      : this.setCarsStorage();
    
    // Calcular el valor total sumando los totalPrice de todos los items
    // Si this._totalPrice está definido, usarlo; sino calcularlo desde los items
    let totalValue = this._totalPrice;
    if (!totalValue && items && items.length > 0) {
      totalValue = items.reduce((sum: number, item: any) => {
        return sum + (this.currentToNumber(item.totalPrice) || 0);
      }, 0);
    }
    
    const eventData = {
      Intencion: 'Compra',
      ecommerce: {
        transaction_id: transaction,
        value: totalValue,
        currency: 'PEN',
        items: items,
      },
    };
    this.pushEvent(eventData, 'purchase');
  }

  purchaseRequestSent(payload: {
    value: number;
    items: PurchaseRequestSentItem[];
  }) {
    const items = (payload.items || []).map((item) => ({
      item_name: item.item_name,
      item_id: item.item_id,
      item_brand: item.item_brand,
      item_category: item.item_category,
      item_list_name: item.item_list_name,
      item_list_id: item.item_list_id,
      quantity: item.quantity,
      index: item.index,
      discount: item.discount,
      _discount: item._discount,
      price: item.price,
      totalPrice: item.totalPrice,
      ...this.pickPresentationFieldsFromProduct(item),
    }));

    const eventData = {
      // Los campos comunes (TimeStamp, Url, TipoDispositivo, etc.) se setean en pushEvent()
      Intencion: 'Compra',
      ecommerce: {
        value: payload.value,
        currency: 'PEN',
        items,
      },
    };

    this.pushEvent(eventData, 'purchase_request_sent');
  }

  purchaseRequestFailed(payload: {
    value: number;
    item: PurchaseFailedItem;
    error_type: string;
    error_code: string;
    error_message: string;
  }) {
    const eventData = {
      Intencion: 'Compra',
      ecommerce: {
        value: payload.value,
        currency: 'PEN',
        items: [
          {
            item_name: payload.item.item_name,
            item_id: payload.item.item_id,
            item_brand: payload.item.item_brand,
            item_category: payload.item.item_category,
            quantity: payload.item.quantity,
            price: payload.item.price,
            totalPrice: payload.item.totalPrice,
            ...this.pickPresentationFieldsFromProduct(payload.item),
          },
        ],
      },
      error: {
        error_type: payload.error_type,
        error_code: payload.error_code,
        error_message: payload.error_message,
      },
    };

    this.pushEvent(eventData, 'purchase_request_failed');
  }

  purchaseFailed(payload: {
    value: number;
    item: PurchaseFailedItem;
    error_type: string;
    error_code: string;
    error_message: string;
  }) {
    const eventData = {
      Intencion: 'Compra',
      ecommerce: {
        value: payload.value,
        currency: 'PEN',
        items: [
          {
            item_name: payload.item.item_name,
            item_id: payload.item.item_id,
            item_brand: payload.item.item_brand,
            item_category: payload.item.item_category,
            quantity: payload.item.quantity,
            price: payload.item.price,
            totalPrice: payload.item.totalPrice,
            ...this.pickPresentationFieldsFromProduct(payload.item),
          },
        ],
      },
      error: {
        error_type: payload.error_type,
        error_code: payload.error_code,
        error_message: payload.error_message,
      },
    };

    this.pushEvent(eventData, 'purchase_failed');
  }

  //Intencion de compra
  purchaseIntention(pedido, accion, producto) {
    const items = this._products
      ? this.setCars(this._products)
      : this.setCarsStorage();
    let suggestedProduct;
    let estado;
    let cantidadActualizada;
    let precioActualizada;
    let stockMax = Number(producto.stockReal);
    if (producto.replace) {
      suggestedProduct = producto.replace;
    } else if (producto.add) {
      suggestedProduct = producto.add;
    } else {
      suggestedProduct = undefined;
    }
    if (producto.textoProducto == 'Sin Stock') {
      estado = 'Sin Stock';
    } else if (producto.textoProducto == 'Sin Suficiente stock') {
      estado = 'Stock Parcial';
    } else if (accion == 'Modifico cantidades') {
      if (stockMax == 0) {
        estado = 'Sin Stock';
      } else {
        estado = 'Stock Parcial';
      }
    }
    const skuReemplazado = Number(suggestedProduct?.sku) || 0;
    const skuSolicitado = Number(producto.sku);
    const cantidadReemplazado =
      (suggestedProduct?.InvoicePrice
        ? this.currentToNumber(suggestedProduct?.InvoicePrice?.quantity)
        : this.currentToNumber(suggestedProduct?.count)) || 0;
    const MontoPrecioSugerido =
      (suggestedProduct?.InvoicePrice
        ? this.currentToNumber(suggestedProduct?.InvoicePrice?.subTotal)
        : this.currentToNumber(suggestedProduct?.totalPrice)) || 0;
    items.forEach((item: any) => {
      if (item.item_id == skuReemplazado) {
        cantidadActualizada =
          item.quantity == cantidadReemplazado
            ? cantidadReemplazado
            : item.quantity;
        precioActualizada =
          item.quantity == cantidadReemplazado
            ? MontoPrecioSugerido
            : item.totalPrice;
      } else if (accion == 'Modifico cantidades') {
        if (item.item_id == skuSolicitado) {
          cantidadActualizada = item.quantity;
          // Calcular el monto sugerido basado en la nueva cantidad
          const precioUnitario = producto.InvoicePrice 
            ? this.currentToNumber(producto.InvoicePrice.price)
            : this.currentToNumber(producto.salePrice) || this.currentToNumber(producto.cashPrice);
          precioActualizada = Number.parseFloat((precioUnitario * cantidadActualizada).toFixed(2));
        }
      }
    });
    const eventData = {
      Pedido: String(pedido),
      Estado: estado,
      SKUSolicitado: String(skuSolicitado),
      DescSKU: producto.description,
      Cantidad: producto.InvoicePrice
        ? this.currentToNumber(producto.InvoicePrice.quantity)
        : this.currentToNumber(producto.count),
      StockMax: stockMax,
      MontoPrecio: producto.InvoicePrice
        ? this.currentToNumber(producto.InvoicePrice.subTotal)
        : this.currentToNumber(producto.totalPrice),
      Accion: accion,
      SKUReemplazante: suggestedProduct?.sku ? String(suggestedProduct.sku) : '',
      DescSKUReemplazado: suggestedProduct?.description || '',
      CantidadReemplazado: cantidadActualizada || 0,
      MontoPrecioSugerido: precioActualizada || 0,
    };
    this.pushEvent(eventData);
  }
  // new events
  clickSalesAdvanceEvent() {
    const eventData = {
      ProcesoGeneral: 'Avance de ventas',
      CTA: 'Los infaltables',
      Seccion: 'Header',
      SubSeccion: 'Mi cuenta'
    };
    this.pushEvent(eventData);
  }
  clickCarouselInfaltablesEvent(buttonText: string) {
    const eventData = {
      ProcesoGeneral: 'Flujo de compra',
      CTA: buttonText,
      Seccion: 'Home',
      SubSeccion: 'Carrusel Los Infaltables'
    };
    this.pushEvent(eventData);
  }
  viewCarouselInfaltablesEvent() {
    const eventData = {
      ProcesoGeneral: 'Flujo de compra',
      Seccion: 'Home',
      SubSeccion: 'Carrusel Los Infaltables'
    };
    this.pushEvent(eventData);
  }
  clickBannerInfaltablesEvent(buttonText: string) {
    const eventData = {
      ProcesoGeneral: 'Flujo de compra',
      CTA: buttonText,
      Seccion: 'Confirmación de Pedido',
      SubSeccion: 'Banner Los Infaltables'
    };
    this.pushEvent(eventData);
  }

  clickTermsConditionsEvent(buttonText: string) {
    const eventData = {
      ProcesoGeneral: 'Términos y Condiciones',
      CTA: buttonText,
      Seccion: 'Términos y Condiciones',
      SubSeccion: 'Acepto términos y condiciones'
    };
    this.pushEvent(eventData);
  }

  createItemsWithPrices(products: any[], priceConfig?: any): EcommerceItemWithPrices[] {
    const patched = Array.isArray(products)
      ? this.enrichProductsArrayForPresentation(products)
      : [];
    return patched.map((product, index) => {
      // Configuración de precios específica para este producto
      const config = priceConfig?.[product.sku] || priceConfig?.default || {};
      
      // Determinar qué campos usar para los precios
      const price = this.currentToNumber(
        config.price || product.InvoicePrice?.price || product.discountedPrice || product.salePrice || product.cashPrice
      );
      
      const totalPrice = this.currentToNumber(
        config.totalPrice || product.InvoicePrice?.subTotal || product.totalPrice
      );
      
      const discount = config.discount !== undefined 
        ? config.discount 
        : this.calculateDiscount(product);

      return {
        item_name: product.description,
        item_id: String(this.currentToNumber(product.sku)),
        item_brand: product.brandName || product.brand_name,
        item_category: product.categoryName || product.category_name,
        item_list_name: product.item_list_name || 'Generales',
        item_list_id: product.item_list_id || 'GENERAL',
        price: price,
        totalPrice: totalPrice,
        quantity: typeof product.count === 'string'
          ? Number.parseInt(product.count.replace(',', ''), 10)
          : product.count,
        index: index,
        discount: discount,
        _discount: discount,
        ...this.pickPresentationFieldsFromProduct(product),
      };
    });
  }

  combineItemsWithPriceFields(products: any[], priceFields: PriceFields[]): EcommerceItemWithPrices[] {
    const baseItems = this.setCars(products);
    
    return baseItems.map((item, index) => {
      const priceField = priceFields[index];
      const pres = this.pickPresentationFieldsFromProduct(products[index]);
      if (!priceField) {
        return { ...item, ...pres } as EcommerceItemWithPrices;
      }
      
      return {
        ...item,
        price: priceField.price,
        totalPrice: priceField.totalPrice,
        discount: priceField.discount,
        _discount: priceField.discount,
        ...pres,
      } as EcommerceItemWithPrices;
    });
  }
}
